<template>
  <div>
    <v-dialog
      v-model="show"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <v-card v-if="!deleteCheck" class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 v-if="financialAsset.id" class="text-h5">
                Update an investment
              </h2>
              <h2 v-else class="text-h5">Add a financial asset</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <ValidationObserver ref="observer">
            <v-form class="modal-form">
              <ValidationProvider
                id="investment_type"
                v-slot="{ errors }"
                name="investment_type"
                rules="required"
              >
                <ListSelect
                  v-model="financialAsset.investmentType"
                  :items="$options.investmentTypes"
                  :name="'investment_type'"
                  :label="'Type of investment*'"
                  :errors="errors"
                />
              </ValidationProvider>
              <ValidationProvider
                id="other_investment_type"
                v-slot="{ errors }"
                name="other_investment_type"
                :rules="{ required: otherInvestmentTypeRequired }"
              >
                <TextField
                  v-if="otherInvestmentTypeRequired"
                  v-model.trim="financialAsset.otherInvestmentType"
                  :label="'Other investment type*'"
                  :errors="errors"
                  :name="'other_investment_type'"
                />
              </ValidationProvider>
              <ValidationProvider
                id="institution"
                v-slot="{ errors }"
                name="institution"
                rules="required"
              >
                <TextField
                  v-model.trim="financialAsset.institution"
                  :label="'Institution*'"
                  :errors="errors"
                  :name="'institution'"
                />
              </ValidationProvider>
              <ValidationProvider
                id="account_number"
                v-slot="{ errors }"
                name="account_number"
                :rules="{ required: false }"
              >
                <TextField
                  v-model.trim="financialAsset.accountNumber"
                  :label="'Account/Policy Number (optional)'"
                  :errors="errors"
                  :name="'account_number'"
                />
              </ValidationProvider>
            </v-form>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col v-if="financialAsset.id" cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save'"
                  :small="true"
                  :callout="'right'"
                  @gradientButtonClicked="save"
                  :loading="buttonLoading"
                />
                <GradientButton
                  :title="'Remove Investment'"
                  :small="true"
                  :fill="false"
                  @gradientButtonClicked="deleteCheck = true"
                />
              </div>
            </v-col>
            <v-col v-else cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save And Continue'"
                  :small="true"
                  :callout="'right'"
                  :loading="buttonLoading"
                  @gradientButtonClicked="save"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>

      <DeleteDialog
        v-if="deleteCheck"
        :content="deleteDialogContent"
        @delete="deleteFinancialAsset"
        @dialogClosed="closeDialog"
      />
    </v-dialog>

    <ErrorSnackbar v-model="showError" />
  </div>
</template>

<script>
  import {
    CREATE_FINANCIAL_ASSET_MUTATION,
    UPDATE_FINANCIAL_ASSET_MUTATION,
    DELETE_FINANCIAL_ASSET_MUTATION,
  } from '../../constants/graphql/financial-assets';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import DeleteDialog from '../common/DeleteDialog.vue';
  import {
    investmentTypeOptions,
    financialAssetType,
  } from '../../constants/js/financial-assets';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'FinancialAssetDialog',
    components: {
      DeleteDialog,
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      value: Boolean,
      financialAssetToUpdate: Object,
    },
    data() {
      return {
        financialAsset: {
          investmentType: null,
          otherInvestmentType: null,
          institution: null,
          accountNumber: null,
        },
        deleteCheck: false,
      };
    },
    investmentTypes: investmentTypeOptions,
    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      deleteDialogContent() {
        const text =
          'Are you sure you want to remove this investment?<br><br><b>' +
          this.financialAssetListName +
          '</b>';
        return {
          title: 'Remove investment',
          text: text,
          buttonText: 'Yes, Remove Investment',
        };
      },
      otherInvestmentTypeRequired() {
        return this.financialAsset.investmentType == 'OTHER';
      },
      financialAssetListName() {
        return (
          financialAssetType(this.financialAsset) +
          ' - ' +
          this.financialAsset.institution
        );
      },
    },
    watch: {
      value: function (dialogOpen) {
        if (dialogOpen) {
          // using native JSON functions removes reactivity
          // so we can clone an object without mutating the original source
          this.financialAsset = JSON.parse(
            JSON.stringify(this.financialAssetToUpdate)
          );
        }
      },
    },
    methods: {
      save() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            if (this.financialAsset.id) {
              this.updateFinancialAsset();
            } else {
              this.createFinancialAsset();
            }
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      createFinancialAsset() {
        const financialAssetInputDetails = {
          testatorId: this.testatorId,
          investmentType: this.financialAsset.investmentType,
          otherInvestmentType: this.financialAsset.otherInvestmentType,
          institution: this.financialAsset.institution,
          accountNumber: this.financialAsset.accountNumber,
        };

        this.$apollo
          .mutate({
            mutation: CREATE_FINANCIAL_ASSET_MUTATION,
            variables: {
              financialAssetInputDetails,
            },
            update: (cache, { data: { createFinancialAsset } }) => {
              this.updateCacheAfterCreateFinancialAsset(
                cache,
                createFinancialAsset
              );
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterCreateFinancialAsset(cache, createFinancialAsset) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.financialAssets.push(createFinancialAsset);

        cache.writeQuery({ ...query, data: data });
      },
      updateFinancialAsset() {
        const financialAssetInputDetails = {
          investmentType: this.financialAsset.investmentType,
          otherInvestmentType: this.financialAsset.otherInvestmentType,
          institution: this.financialAsset.institution,
          accountNumber: this.financialAsset.accountNumber,
        };
        const id = this.financialAsset.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_FINANCIAL_ASSET_MUTATION,
            variables: {
              id,
              financialAssetInputDetails,
            },
            update: (cache, { data: { updateFinancialAsset } }) => {
              this.updateCacheAfterUpdateFinancialAsset(
                cache,
                updateFinancialAsset
              );
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterUpdateFinancialAsset(cache, updateFinancialAsset) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var financialAsset = data.testator.financialAssets.find(
          financialAsset => financialAsset.id == updateFinancialAsset.id
        );
        var financialAsset = updateFinancialAsset;

        cache.writeQuery({ ...query, data: data });
      },
      deleteFinancialAsset() {
        const id = this.financialAsset.id;

        this.$apollo
          .mutate({
            mutation: DELETE_FINANCIAL_ASSET_MUTATION,
            variables: {
              id,
            },
            update: (cache, { data: { deleteFinancialAsset } }) => {
              this.updateCacheAfterDeleteFinancialAsset(
                cache,
                deleteFinancialAsset
              );
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterDeleteFinancialAsset(cache, deleteFinancialAsset) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var financialAssets = data.testator.financialAssets;
        var index = financialAssets.findIndex(
          financialAsset => financialAsset.id == deleteFinancialAsset.id
        );
        financialAssets.splice(index, 1);

        cache.writeQuery({ ...query, data: data });
      },
      closeDialog() {
        this.show = false;
        setTimeout(() => this.clearDialog(), 300);
      },
      clearDialog() {
        this.buttonLoading = false;
        this.financialAsset = {};
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
        this.deleteCheck = false;
      },
    },
  };
</script>
<style lang="scss" scoped>
  .button-container {
    margin-bottom: 22px;
    * {
      margin-right: 15px;
    }
  }
</style>
